import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@elaniintc/goes-design-system";
import { formatToRelativeTime } from "@/utils/dates";
import { useGetUser } from "@/queries/users/users.api";

interface VersionCardProps {
  flowId: string;
  versionNumber?: number;
  createdAt: string;
  createdBy?: string;
  status: string | null;
  comment?: string;
  isRestored?: boolean;
  restoredFromVersion?: number;
  onPreview?: (flowId: string) => void;
  onRestore?: (flowId: string) => void;
  onPublish?: (flowId: string) => void;
  onArchive?: (flowId: string) => void;
  canPublish?: boolean;
  canRestore?: boolean;
}

const UserName = ({ userId }: { userId: string }) => {
  const { data: user, isLoading } = useGetUser(userId);

  if (isLoading) return <span className="text-body-quiet">...</span>;

  const name = user?.name
    ? `${user.name} ${user.last_name ?? ""}`.trim()
    : "Desconocido";

  return <span className="text-action">{name}</span>;
};

const VersionCard = ({
  flowId,
  versionNumber,
  createdAt,
  createdBy,
  status,
  comment,
  isRestored = false,
  restoredFromVersion,
  onRestore,
  onPublish,
  onArchive,
  canPublish = false,
  canRestore = false,
}: VersionCardProps) => {
  const isActive = status === "PUBLISHED" || status === "ACTIVE";

  const getBadgeProps = () => {
    if (status === "ACTIVE" || status === "PUBLISHED") {
      return { variant: "success" as const, label: "Activa" };
    }
    if (status === "DENIED") {
      return { variant: "danger" as const, label: "Rechazada" };
    }
    return null;
  };

  const badgeProps = getBadgeProps();

  return (
    <div className="border border-neutral rounded-lg p-4 flex flex-col gap-3 mb-4 [box-shadow:0px_1px_2px_rgba(17,20,24,0.08)]">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h3 className="font-normal text-body-loud">
              Versión {versionNumber}
              {isRestored && restoredFromVersion && (
                <span className="text-body-quiet font-normal">
                  {" "}
                  (Restaurada de la versión {restoredFromVersion})
                </span>
              )}
            </h3>
          </div>
          <p className="text-sm text-body-quiet">
            Creada {formatToRelativeTime(createdAt) || ""} por{" "}
            <UserName userId={createdBy || ""} />
          </p>
        </div>

        <div className="flex items-center gap-2">
          {badgeProps && (
            <Badge variant={badgeProps.variant} size="sm">
              {badgeProps.label}
            </Badge>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Opciones de versión"
                onClick={(e) => e.stopPropagation()}
              >
                <Icon
                  type="far"
                  name="ellipsis-vertical"
                  className="text-body"
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg border-neutral-silent text-body-loud font-semibold p-2"
              onClick={(e) => e.stopPropagation()}
            >
              {canRestore && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore?.(flowId);
                  }}
                  className="hover:bg-neutral-object-accent-silent font-normal text-body cursor-pointer"
                >
                  Restaurar esta versión
                </DropdownMenuItem>
              )}
              {isActive ? (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive?.(flowId);
                  }}
                  className="hover:bg-neutral-object-accent-silent font-normal text-danger cursor-pointer"
                >
                  Archivar esta versión
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onPublish?.(flowId);
                  }}
                  disabled={!canPublish}
                  className="hover:bg-neutral-object-accent-silent font-normal text-body cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Publicar versión
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {comment && (
        <div className="bg-neutral-silent rounded-lg p-3 flex gap-2 bg-neutral-object-accent-silent [outline:1px_solid_#CFD5DD] -outline-offset-1">
          <Icon
            type="fas"
            name="comment"
            className="text-warning-quiet text-lg shrink-0 mt-0.5"
          />
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-warning-quiet text-sm">
              Comentario:
            </span>
            <p className="text-body whitespace-pre-wrap">{comment}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionCard;
